import {
  Title,
  Text,
  Paper,
  Tabs,
  Divider,
  Flex,
  Box,
  Button,
  LoadingOverlay,
  ThemeIcon,
  Tooltip,
  Modal,
  Checkbox,
} from "@mantine/core";
import PersonalDetails from "../EmptyCompanyProfile/EmptyProfileForms/PersonalDetails";
import BasicCompanyInfo from "../EmptyCompanyProfile/EmptyProfileForms/EmptyBasicInfo";
import BusinessOverview from "../EmptyCompanyProfile/EmptyProfileForms/EmptyBusinessOverview";
import CapabilitiesOfferings from "../EmptyCompanyProfile/EmptyProfileForms/EmptyCapabilities";
import GeographyMarkets from "../EmptyCompanyProfile/EmptyProfileForms/EmptyGeographyMarkets";
import ProfileGenerateModal from "../ProfileGenerateModal/ProfileGenerateModal";
import CreateProfileModal from "../ProfileGenerateModal/CreateProfileModal";
import { useEffect, useCallback, useState, useRef, type ReactNode } from "react";
import { MultiStepLoader } from "@/components/shared/MultiStepLoader";
import { useNavigate } from "react-router-dom";
import {
  buildGroupedCountrySelectOptions,
  getIndustryOptions,
  getRegionOptionsForSelect,
  headquartersCountrySupportsEuVatOption,
  headquartersUsesIndirectCountryRegistrationWeb,
  looksLikeEuVatRegistrationNumber,
} from "@/components/shared/constants/general.const";
import { headquartersValueForForm } from "@/shared/constants/signup.const";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { errorNotification } from "@/shared/utils/errorNotification";
import { useDashboardState } from "./statecomponent/DashboardState";
import {
  type ProfileBuilderDraftV1,
  isProfileBuilderDraftMeaningful,
} from "./profileBuilderDraft.util";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  NAV_TAB_LABEL_FZ,
  navTabsListStyles,
} from "@/shared/styles/navTabsLayout";
import {
  IconGift,
  IconArrowLeft,
  IconAlertCircle,
  IconTrash,
  IconBuilding,
  IconBuildingCommunity,
  IconMapPin,
  IconPackage,
  IconTool,
  IconUser,
  IconSparkles,
  IconDeviceFloppy,
  IconHistory,
} from "@tabler/icons-react";
import ProductsServicesTabContent from "@/components/profile/ProductsServicesTabContent";
import DocumentsTabContent from "@/components/profile/DocumentsTabContent";
import ImagesTabContent from "@/components/profile/ImagesTabContent";

const regionOptions = getRegionOptionsForSelect();

function companyWebsiteFromModalOrAi(sourceUrl: string | undefined, aiWebsite: string | undefined): string {
  const fromModal = (sourceUrl ?? "").trim();
  if (fromModal) {
    if (/^https?:\/\//i.test(fromModal)) return fromModal;
    if (/^www\./i.test(fromModal) || /^[\w.-]+\.\w{2,}/i.test(fromModal)) return `https://${fromModal}`;
    return fromModal;
  }
  return (aiWebsite ?? "").trim();
}

/** Same palette as profile preview sections (ProfileTab / MatchedProfileTab). */
const PROFILE_SECTION = {
  navy: "#04305c",
  gold: "#ffc300",
  panelBg: "#ffffff",
  shadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
  cardBorderWidth: 2,
} as const;

function ProfileFormSection({
  id,
  sectionNumber,
  title,
  description,
  headerIcon,
  children,
}: {
  id: string;
  sectionNumber: number;
  title: string;
  description: string;
  headerIcon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box id={id}>
      <Paper
        p={0}
        radius="lg"
        mb="xl"
        styles={{
          root: {
            overflow: "hidden",
            backgroundColor: PROFILE_SECTION.panelBg,
            boxShadow: PROFILE_SECTION.shadow,
            border: `${PROFILE_SECTION.cardBorderWidth}px solid ${PROFILE_SECTION.navy}`,
          },
        }}
      >
        <Flex
          align="center"
          gap="sm"
          px={{ base: "md", sm: "xl" }}
          py={{ base: "md", sm: "lg" }}
          style={{
            backgroundColor: PROFILE_SECTION.navy,
            width: "100%",
            boxSizing: "border-box",
            /* Inner curve = outer lg radius minus border so navy meets the border arc */
            borderTopLeftRadius: `max(0px, calc(var(--mantine-radius-lg) - ${PROFILE_SECTION.cardBorderWidth}px))`,
            borderTopRightRadius: `max(0px, calc(var(--mantine-radius-lg) - ${PROFILE_SECTION.cardBorderWidth}px))`,
          }}
        >
          {headerIcon}
          <Text fw={700} fz="md" c={PROFILE_SECTION.gold} style={{ letterSpacing: 0.2 }}>
            {sectionNumber}. {title}
          </Text>
        </Flex>
        <Box p={{ base: "lg", sm: "xl" }}>
          <Text fz="sm" c="slate.6" mb="lg">
            {description}
          </Text>
          {children}
        </Box>
      </Paper>
    </Box>
  );
}

interface DashboardPageProps {
  mode?: "create" | "edit";
}

function CreateProfilePage({ mode = "create" }: DashboardPageProps) {
  const navigate = useNavigate();
  const state = useDashboardState(mode);
  const [isGeneratingProfile, setIsGeneratingProfile] = useState(false);
  const [showAuthorityConfirm, setShowAuthorityConfirm] = useState(false);
  const [authorityChecked, setAuthorityChecked] = useState(false);
  const [isAuthoritySaving, setIsAuthoritySaving] = useState(false);
  const [showClearFormConfirm, setShowClearFormConfirm] = useState(false);
  const [showResumeDraftModal, setShowResumeDraftModal] = useState(false);
  const [pendingResumeDraft, setPendingResumeDraft] = useState<ProfileBuilderDraftV1 | null>(null);
  const [isDiscardingResumeDraft, setIsDiscardingResumeDraft] = useState(false);
  const offerAiIntroAfterStartFreshRef = useRef(false);

  const AUTHORITY_CONFIRM_MESSAGE =
    "I hereby confirm that I am authorised to create and manage this account on behalf of the company. I further confirm that all information provided is true and accurate, and I understand that any misrepresentation may result in account suspension and legal action.";

  const countryOptions = buildGroupedCountrySelectOptions();

  const industryOptions = getIndustryOptions();

  // Locked fields configuration for verified companies
  const lockedFieldsWhenVerified = {
    // Basic Company Info
    companyLegalName: true,
    country: true,
    registrationNumber: true,
    registrationIdType: true,
    yearOfIncorporation: true,
    companyEmail: true,
    state: true,

    // Geography & Markets
    headquartersCountry: true,
    headquartersCity: true,

    // Additional Details
    companyRegisteredAddress: true,
    additionalCompanyStatus: true,
    directorName: true,
    directorDesignation: true,
    verificationBusinessType: true,
  };

  const isFieldLocked = (fieldName: string) => {
    // Personal details + company email come from account signup; not editable in profile builder
    if (
      fieldName === "companyEmail" ||
      fieldName === "userName" ||
      fieldName === "userDesignation" ||
      fieldName === "userPhoneNumber"
    ) {
      return true;
    }
    if (mode !== "edit") return false;
    if (!state.isVerified) return false;
    if (state.isAdminBlocked) return false;
    return lockedFieldsWhenVerified[fieldName as keyof typeof lockedFieldsWhenVerified] || false;
  };

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (state.userDetails === undefined) {
        return;
      }

      if (!state.userDetails || !(state.userDetails as any).company) {
        state.setIsPageLoading(false);
        if (mode !== "edit") {
          state.setShowIntroModal(true);
        }
        return;
      }

      const companyIdFromUser = (state.userDetails as any).company.id;
      state.setCompanyId(companyIdFromUser);

      try {
        const company = await trpc.company.getById.query({ companyId: companyIdFromUser });

        if (company) {
          if (mode === "edit") {
            const allowedStatuses = ["VERIFIED", "SYSTEM_VERIFIED", "ACTION_REQUIRED", "DRAFT"];
            if (!allowedStatuses.includes(company.status || "")) {
              navigate("/profile", { replace: true });
              return;
            }
          } else if (mode === "create") {
            if ((company as any).website) {
              navigate("/profile", { replace: true });
              return;
            }
          }

          state.setCompanyStatus(company.status || null);
          state.setCompanyGtid((company as any).gtid || null);
          state.setIsVerified(company.status === "VERIFIED" || company.status === "SYSTEM_VERIFIED");
          state.setIsAdminBlocked((company as any).adminBlocked === true);

          if ((company as any).logo) state.setExistingLogo((company as any).logo);
          if ((company as any).images && (company as any).images.length > 0) {
            state.setExistingImages((company as any).images);
          }

          // Extract Products and Services from company data
          if ((company as any).productServices && Array.isArray((company as any).productServices)) {
            const products = (company as any).productServices.filter((ps: any) => ps.type === "PRODUCT");
            const services = (company as any).productServices.filter((ps: any) => ps.type === "SERVICE");
            state.setProducts(products);
            state.setServices(services);
          }

          // Handle Documents
          if ((company as any).documents) {
            const docs = (company as any).documents;
            // All documents here are treated as optional "additional documents"
            state.setExistingDocs(docs);
          }

          state.setFormData((prev) => ({
            ...prev,
            // Personal Details from User
            userName: (company as any).user?.name || "",
            userDesignation: (company as any).user?.designation || "",
            userPhoneNumber: (company as any).user?.phoneNumber?.replace(/^\+\d+\s*/, "") || "",
            userPhoneCountryCode: (company as any).user?.phoneNumber?.match(/^\+\d+/)?.[0] || "",
            // Company Details
            companyLegalName: (company as any).legalName || "",
            tradeName: (company as any).tradeName || "",
            state: (company as any).state || "",
            registrationNumber: (company as any).registrationNumber || "",
            registrationIdType: (() => {
              const t = (company as any).registrationIdType;
              const hc = headquartersValueForForm((company as any).headquartersCountry);
              if (t) {
                if (t === "COUNTRY_REGISTRATION") return "COUNTRY_REGISTRATION";
                if (hc === "New Zealand" && t === "LOCAL_REGISTRY") return "NZBN";
                if (hc === "China (Mainland)" && t === "LOCAL_REGISTRY") {
                  return headquartersUsesIndirectCountryRegistrationWeb(hc) ? "COUNTRY_REGISTRATION" : "USCC";
                }
                if (
                  headquartersCountrySupportsEuVatOption(hc) &&
                  t === "LOCAL_REGISTRY" &&
                  looksLikeEuVatRegistrationNumber(((company as any).registrationNumber || "") as string)
                ) {
                  return "EU_VAT";
                }
                if (hc === "Taiwan" && t === "LOCAL_REGISTRY") return "UBN";
                if (hc === "United Kingdom" && (t === "LOCAL_REGISTRY" || t === "EU_VAT")) return "CRN";
                if (hc === "Norway" && t === "LOCAL_REGISTRY") return "NOGN";
                if (headquartersUsesIndirectCountryRegistrationWeb(hc) && t === "LOCAL_REGISTRY") {
                  return "COUNTRY_REGISTRATION";
                }
                return t;
              }
              const reg = ((company as any).registrationNumber || "").trim().toUpperCase();
              if (hc === "India") return null;
              if (/^[0-9A-Z]{18}[0-9]{2}$/.test(reg)) return "LEI";
              if (reg) {
                if (hc === "New Zealand") return "NZBN";
                if (hc === "China (Mainland)") {
                  return headquartersUsesIndirectCountryRegistrationWeb(hc) ? "COUNTRY_REGISTRATION" : "USCC";
                }
                if (headquartersCountrySupportsEuVatOption(hc) && looksLikeEuVatRegistrationNumber(reg)) {
                  return "EU_VAT";
                }
                if (hc === "Taiwan") return "UBN";
                if (hc === "United Kingdom") return "CRN";
                if (hc === "Norway") return "NOGN";
                if (headquartersUsesIndirectCountryRegistrationWeb(hc)) return "COUNTRY_REGISTRATION";
                return "LOCAL_REGISTRY";
              }
              if (hc === "New Zealand") return "NZBN";
              if (hc === "China (Mainland)") {
                return headquartersUsesIndirectCountryRegistrationWeb(hc) ? "COUNTRY_REGISTRATION" : "USCC";
              }
              if (headquartersCountrySupportsEuVatOption(hc)) return "EU_VAT";
              if (hc === "Taiwan") return "UBN";
              if (hc === "United Kingdom") return "CRN";
              if (hc === "Norway") return "NOGN";
              if (headquartersUsesIndirectCountryRegistrationWeb(hc)) return "COUNTRY_REGISTRATION";
              return "LOCAL_REGISTRY";
            })(),
            yearOfIncorporation: (company as any).yearOfIncorporation
              ? new Date((company as any).yearOfIncorporation, 0, 1)
              : null,
            companyWebsite: (company as any).website || "",
            companyEmail:
              (company as any).email?.trim() ||
              (company as any).user?.email?.trim() ||
              "",
            phoneCountryCode: (company as any).phoneCountryCode || "",
            phoneNumber: (company as any).phoneNumber || "",
            companyDescription: (company as any).description || "",
            industryCategory: (company as any).industryCategory || [],
            subIndustry: (company as any).subIndustry || "",
            whoAmI: (company as any).whoAmI || [],
            collaborationIntent: (company as any).collaborationIntent || [],
            coreCapabilities: (company as any).coreCapabilities || [],
            secondaryCapabilities: (company as any).secondaryCapabilities || [],
            keyDifferentiators: (company as any).keyDifferentiators || "",
            technologies: (company as any).technologies || [],
            additionalTechInfrastructure: (company as any).additionalTechInfrastructure || [],
            country: headquartersValueForForm((company as any).headquartersCountry),
            headquartersCountry: headquartersValueForForm((company as any).headquartersCountry),
            headquartersCity: headquartersValueForForm((company as any).headquartersCity),
            currentAddress: (company as any).currentAddress || "",
            operatingCountries: (company as any).operatingCountries || [],
            preferredRegions: (company as any).preferredCollaborationRegions || [],
            companyRegisteredAddress: (company as any).additionalDetails?.companyRegisteredAddress || "",
            nationalIndustryClassificationCode:
              (company as any).additionalDetails?.nationalIndustryClassificationCode || "",
            additionalCompanyStatus: (company as any).additionalDetails?.companyStatus || "",
            directorName: (company as any).additionalDetails?.directorName || "",
            directorDesignation: (company as any).additionalDetails?.directorDesignation || "",
            directorEmail: (company as any).additionalDetails?.directorEmail || "",
            paidupCapitalBand: (company as any).additionalDetails?.paidupCapitalBand || "",
            turnoverBand: (company as any).additionalDetails?.turnoverBand || "",
            verificationBusinessType: (company as any).additionalDetails?.verificationBusinessType || "",
          }));

          if (mode === "create") {
            const shouldOfferAiIntro = !(company as any).description;
            let showedResumeDraftModal = false;
            try {
              const draftRes = await trpc.company.getProfileBuilderDraft.query({
                companyId: companyIdFromUser,
              });
              if (
                draftRes?.data != null &&
                isProfileBuilderDraftMeaningful(draftRes.data)
              ) {
                showedResumeDraftModal = true;
                setPendingResumeDraft(draftRes.data as unknown as ProfileBuilderDraftV1);
                setShowResumeDraftModal(true);
                if (shouldOfferAiIntro) {
                  offerAiIntroAfterStartFreshRef.current = true;
                }
              }
            } catch {
              // ignore draft fetch errors
            }
            if (shouldOfferAiIntro && !showedResumeDraftModal) {
              state.setShowIntroModal(true);
            }
          }
        }
      } catch (err) {
        handleTrpcError(err);
      } finally {
        state.setIsPageLoading(false);
      }
    };

    fetchCompanyData();
  }, [state.userDetails, navigate]);

  const toStringArray = (val: any): string[] => {
    if (!val) return [];
    if (typeof val === "string") return [val];
    if (!Array.isArray(val)) return [];
    return val.map((item) => (typeof item === "string" ? item : String(item?.name ?? item?.value ?? item ?? "")));
  };

  const handleAiProfileGenerated = useCallback(
    (data: any, meta?: { sourceUrl: string }) => {
      const aiCompanyEmail =
        (typeof data.data?.company_email === "string" && data.data.company_email.trim()) ||
        (typeof data.data?.director_email === "string" && data.data.director_email.trim()) ||
        "";
      const accountEmail = (state.userDetails as { email?: string } | null | undefined)?.email?.trim() || "";

      const preserveVerifiedIdentity =
        mode === "edit" && state.isVerified && !state.isAdminBlocked;

      state.setFormData((prev) => {
        const next = {
          ...prev,
          companyLegalName: data.data.legal_name || "",
          tradeName: data.data.trade_name || "",
          companyDescription: data.data.company_description || "",
          industryCategory: toStringArray(data.data.industry_category),
          subIndustry: toStringArray(data.data.sub_industry),
          country: data.data.country || "",
          headquartersCountry: data.data.country || "",
          headquartersCity: data.data.city || "",
          yearOfIncorporation: data.data.year_of_incorporation ? new Date(data.data.year_of_incorporation, 0, 1) : null,
          companyWebsite: companyWebsiteFromModalOrAi(meta?.sourceUrl, data.data.company_website),
          companyEmail: prev.companyEmail?.trim() || aiCompanyEmail || accountEmail,
          phoneNumber: data.data.phone_number || "",
          directorName: data.data.director_name || "",
          directorEmail: data.data.director_email || "",
          directorDesignation: data.data.director_designation || "",
          coreCapabilities: toStringArray(data.data.core_capabilities),
          secondaryCapabilities: toStringArray(data.data.secondary_capabilities),
          keyDifferentiators: data.data.key_differentiators || "",
          technologies: toStringArray(data.data.technologies_infrastructure),
          operatingCountries: toStringArray(data.data.operating_countries),
          preferredRegions: toStringArray(data.data.preferred_collaboration_regions),
          collaborationIntent: toStringArray(data.data.collaboration_intent),
        };

        if (preserveVerifiedIdentity) {
          next.companyLegalName = prev.companyLegalName;
          next.country = prev.country;
          next.headquartersCountry = prev.headquartersCountry;
          next.headquartersCity = prev.headquartersCity;
          next.yearOfIncorporation = prev.yearOfIncorporation;
          next.companyEmail = prev.companyEmail;
          next.directorName = prev.directorName;
          next.directorDesignation = prev.directorDesignation;
        }

        return next;
      });

      if (data.s3_urls && data.s3_urls.image_urls && Array.isArray(data.s3_urls.image_urls)) {
        state.setAiImages(data.s3_urls.image_urls);
      }

      if (data.data.products && Array.isArray(data.data.products)) {
        const newProducts = data.data.products.map((p: any) => ({
          clientId: `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: p.name,
          description: p.description,
        }));
        state.setProducts(newProducts);
      }

      if (data.data.services && Array.isArray(data.data.services)) {
        const newServices = data.data.services.map((s: any) => ({
          clientId: `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: s.name,
          description: s.description,
        }));
        state.setServices(newServices);
      }

      state.setActiveTab("company-profile");
    },
    [state, mode]
  );

  const getSectionIdForField = (fieldName: string, tab: string) => {
    if (tab === "images") {
      if (fieldName.toLowerCase().includes("additional doc")) return "documents-section";
      return "images-section";
    }

    if (
      [
        "Name",
        "Designation",
        "Company Legal Name",
        "Country",
        "Registration ID Type",
        "Registration Number",
        "Company Website",
        "Company Email",
        "Phone Number",
        "Headquarters Country",
        "Headquarters City",
      ].includes(fieldName)
    ) {
      return "basic-company-info-section";
    }
    if (["Company Description", "Industry Category", "Who Am I?"].includes(fieldName)) {
      return "business-overview-section";
    }
    if (fieldName.startsWith("Product ") || fieldName.startsWith("Service ")) {
      return "products-services-section";
    }
    if (fieldName === "Core Capabilities") return "capabilities-offerings-section";
    if (fieldName.includes("verification")) {
      return "personal-details-section";
    }
    return "basic-company-info-section";
  };

  const handleTopSubmitClick = () => {
    const companyErrors = state.invalidFields["company-profile"] || [];
    const imageErrors = state.invalidFields.images || [];

    if (companyErrors.length > 0 || imageErrors.length > 0) {
      const targetTab = companyErrors.length > 0 ? "company-profile" : "images";
      const firstMissingField = companyErrors[0] || imageErrors[0];
      const sectionId = getSectionIdForField(firstMissingField, targetTab);

      state.setActiveTab(targetTab);
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);

      errorNotification({
        title: "Required field missing",
        message: `${firstMissingField} is required.`,
      });
      return;
    }

    if (mode === "create") {
      setShowAuthorityConfirm(true);
      return;
    }

    document.getElementById("create-profile-submit-btn")?.click();
  };

  const handleAuthorityAcceptAndSubmit = async () => {
    if (!state.companyId) {
      errorNotification({ title: "Error", message: "Company not found. Please try again." });
      return;
    }
    if (!authorityChecked) {
      errorNotification({ title: "Confirmation required", message: "Please accept the confirmation to continue." });
      return;
    }

    setIsAuthoritySaving(true);
    try {
      await trpc.company.recordAuthorityAcceptance.mutate({
        companyId: state.companyId,
        message: AUTHORITY_CONFIRM_MESSAGE,
      });
      setShowAuthorityConfirm(false);
      document.getElementById("create-profile-submit-btn")?.click();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setIsAuthoritySaving(false);
    }
  };

  const handleOpenCreateModal = useCallback(() => {
    state.setShowIntroModal(false);
    state.setShowCreateModal(true);
  }, [state]);

  return (
    <Flex direction={"column"} style={{ height: "100vh", overflow: "hidden" }}>
      <LoadingOverlay visible={state.isPageLoading} overlayProps={{ blur: 2 }} />
      <Modal
        opened={showClearFormConfirm}
        onClose={() => setShowClearFormConfirm(false)}
        title="Clear profile draft?"
        centered
        size="md"
      >
        <Text fz="sm" c="slate.7" mb="lg" style={{ lineHeight: 1.5 }}>
          This removes all information you entered on this page—company details, capabilities, geography, products,
          services, gallery images, and extra documents. Your account name, designation, phone, and company email stay
          as they are on your account. Uploaded files that are cleared here may be permanently removed. This cannot be
          undone.
        </Text>
        <Flex justify="flex-end" gap="sm">
          <Button variant="outline" color="gray" onClick={() => setShowClearFormConfirm(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={() => {
              state.resetProfileBuilderDraft();
              setAuthorityChecked(false);
              setShowClearFormConfirm(false);
            }}
          >
            Clear form
          </Button>
        </Flex>
      </Modal>

      <Modal
        opened={showResumeDraftModal}
        onClose={() => undefined}
        title="Continue your profile?"
        centered
        size="md"
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
      >
        <Text fz="sm" c="slate.7" mb="lg" style={{ lineHeight: 1.55 }}>
          You have a saved draft for this company profile. Do you want to continue where you left off, or start fresh
          from your current account details?
        </Text>
        <Flex justify="flex-end" gap="sm" wrap="wrap">
          <Button
            variant="outline"
            color="gray"
            disabled={isDiscardingResumeDraft}
            onClick={async () => {
              const id = state.companyId;
              if (!id) {
                offerAiIntroAfterStartFreshRef.current = false;
                setShowResumeDraftModal(false);
                setPendingResumeDraft(null);
                return;
              }
              setIsDiscardingResumeDraft(true);
              try {
                await trpc.company.deleteProfileBuilderDraft.mutate({ companyId: id });
              } catch {
                // still close modal; user can continue on current form
              } finally {
                setIsDiscardingResumeDraft(false);
                setShowResumeDraftModal(false);
                setPendingResumeDraft(null);
                if (offerAiIntroAfterStartFreshRef.current) {
                  offerAiIntroAfterStartFreshRef.current = false;
                  state.setShowIntroModal(true);
                }
              }
            }}
            loading={isDiscardingResumeDraft}
          >
            Start fresh
          </Button>
          <Button
            onClick={() => {
              offerAiIntroAfterStartFreshRef.current = false;
              state.setShowIntroModal(false);
              if (pendingResumeDraft) {
                state.applySavedProfileBuilderDraft(pendingResumeDraft);
              }
              setShowResumeDraftModal(false);
              setPendingResumeDraft(null);
            }}
            disabled={isDiscardingResumeDraft || !pendingResumeDraft}
            leftSection={<IconHistory size={18} />}
          >
            Continue from saved draft
          </Button>
        </Flex>
      </Modal>

      <Modal
        opened={showAuthorityConfirm}
        onClose={() => {
          if (isAuthoritySaving) return;
          setShowAuthorityConfirm(false);
        }}
        title="Confirmation"
        centered
        size="lg"
        closeOnClickOutside={!isAuthoritySaving}
        closeOnEscape={!isAuthoritySaving}
      >
        <Text fz="sm" c="slate.7" mb="md" style={{ lineHeight: 1.5 }}>
          {AUTHORITY_CONFIRM_MESSAGE}
        </Text>

        <Checkbox
          checked={authorityChecked}
          onChange={(e) => setAuthorityChecked(e.currentTarget.checked)}
          label="I accept and confirm the above statement"
          mb="lg"
          disabled={isAuthoritySaving}
        />

        <Flex justify="flex-end" gap="sm">
          <Button
            variant="outline"
            color="gray"
            onClick={() => setShowAuthorityConfirm(false)}
            disabled={isAuthoritySaving}
          >
            DENY
          </Button>
          <Button onClick={handleAuthorityAcceptAndSubmit} loading={isAuthoritySaving}>
            Accept & Create Profile
          </Button>
        </Flex>
      </Modal>

      <Flex
        p="lg"
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap="sm"
      >
        <Flex align="center" gap="md" style={{ flexShrink: 0 }}>
          {mode === "edit" && (
            <Button
              variant="outline"
              color="gray"
              p={8}
              onClick={() => navigate("/profile")}
              styles={{
                root: {
                  borderColor: "#E5E7EB",
                  borderRadius: 8,
                  minWidth: "auto",
                  height: "auto",
                },
              }}
            >
              <IconArrowLeft size={18} color="#374151" />
            </Button>
          )}
          <Box>
            <Title order={2} fz={24} fw={400}>
              {mode === "edit" ? "Edit Company Profile" : "Company Profile Builder"}
            </Title>
            {mode !== "edit" && (
              <Text c="slate.6" fz="sm">
                Complete your profile to unlock collaboration discovery
              </Text>
            )}
          </Box>
        </Flex>

        <Flex gap="sm" mt={{ base: "sm", md: 0 }} style={{ flexShrink: 0 }} wrap="wrap" justify={{ base: "stretch", md: "flex-end" }}>
          {mode !== "edit" && (
            <>
              <Button
                variant="outline"
                color="gray"
                leftSection={<IconTrash size={18} />}
                onClick={() => setShowClearFormConfirm(true)}
                disabled={state.isPageLoading || state.isSubmitting}
                styles={{
                  root: {
                    borderColor: "#E5E7EB",
                    borderRadius: 8,
                  },
                }}
              >
                Clear form
              </Button>
              <Button
                variant="light"
                leftSection={<IconDeviceFloppy size={18} />}
                onClick={() => void state.saveProfileBuilderDraftToServer()}
                disabled={state.isPageLoading || state.isSubmitting || !state.companyId}
                loading={state.isSavingBuilderDraft}
              >
                Save draft
              </Button>
              <Button
                leftSection={<IconSparkles size={18} />}
                onClick={() => state.setShowIntroModal(true)}
                disabled={state.isPageLoading || state.isSubmitting}
              >
                Generate with AI
              </Button>
            </>
          )}
          {mode === "edit" && (
            <Button
              variant="light"
              leftSection={<IconSparkles size={18} />}
              onClick={() => state.setShowIntroModal(true)}
              disabled={state.isPageLoading || state.isSubmitting}
            >
              Update with AI
            </Button>
          )}
          <Button onClick={handleTopSubmitClick}>{mode === "edit" ? "Save" : "Create Profile"}</Button>
        </Flex>
      </Flex>

      <Divider />

      <Flex align="flex-start" px="md" py="xl" style={{ flex: 1, minHeight: 0 }}>
        <Box w="100%" style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          <Tabs
            value={state.activeTab}
            onChange={state.setActiveTab}
            style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
          >
            <Box w="100%" style={NAV_TABS_FULL_WIDTH_LINE_STYLE}>
              <Tabs.List styles={navTabsListStyles(2, { paddingLeft: 0, paddingRight: 0 })}>
              <Tabs.Tab
                value="company-profile"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  backgroundColor: state.activeTab === "company-profile" ? "#04305c" : "#ebf0ff",
                  color: state.activeTab === "company-profile" ? "white" : "var(--mantine-color-slate-6)",
                  borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
                  border: state.activeTab === "company-profile" ? "1px solid #04305c" : "1px solid #E5E7EB",
                  borderBottom: "none",
                  whiteSpace: "nowrap",
                  fontWeight: state.activeTab === "company-profile" ? 500 : 400,
                  cursor: "pointer",
                  minHeight: 44,
                }}
                rightSection={
                  <Box
                    w={28}
                    h={28}
                    miw={28}
                    mih={28}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  >
                    {state.invalidFields["company-profile"]?.length > 0 ? (
                      <Tooltip
                        label={
                          <Box>
                            <Text fz="xs" fw={700} mb={4} c="white">
                              Missing required fields:
                            </Text>
                            {state.invalidFields["company-profile"].map((field) => (
                              <Text key={field} fz="xs" c="gray.3">
                                • {field}
                              </Text>
                            ))}
                          </Box>
                        }
                        position="top"
                        withArrow
                        multiline
                        color="dark.8"
                      >
                        <ThemeIcon size="xs" color="red" variant="transparent" style={{ cursor: "pointer" }}>
                          <IconAlertCircle size={20} />
                        </ThemeIcon>
                      </Tooltip>
                    ) : null}
                  </Box>
                }
              >
                Company Profile
              </Tabs.Tab>
              <Tabs.Tab
                value="images"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={{
                  ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
                  backgroundColor: state.activeTab === "images" ? "#04305c" : "#ebf0ff",
                  color: state.activeTab === "images" ? "white" : "var(--mantine-color-slate-6)",
                  borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
                  border: state.activeTab === "images" ? "1px solid #04305c" : "1px solid #E5E7EB",
                  borderBottom: "none",
                  whiteSpace: "nowrap",
                  fontWeight: state.activeTab === "images" ? 500 : 400,
                  cursor: "pointer",
                  minHeight: 44,
                }}
                rightSection={
                  <Box
                    w={28}
                    h={28}
                    miw={28}
                    mih={28}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  >
                    {state.invalidFields["images"]?.length > 0 ? (
                      <Tooltip
                        label={
                          <Box>
                            <Text fz="xs" fw={700} mb={4} c="white">
                              Missing required fields:
                            </Text>
                            {state.invalidFields["images"].map((field) => (
                              <Text key={field} fz="xs" c="gray.3">
                                • {field}
                              </Text>
                            ))}
                          </Box>
                        }
                        position="top"
                        withArrow
                        multiline
                        color="dark.8"
                      >
                        <ThemeIcon size="xs" color="red" variant="transparent" style={{ cursor: "pointer" }}>
                          <IconAlertCircle size={20} />
                        </ThemeIcon>
                      </Tooltip>
                    ) : null}
                  </Box>
                }
              >
                Images and Documents
              </Tabs.Tab>
              </Tabs.List>
            </Box>

            <Box style={{ flex: 1, overflow: "auto", backgroundColor: PROFILE_SECTION.panelBg }}>
              <Tabs.Panel value="company-profile" px={0} pt="md" pb="md">
                <ProfileFormSection
                  id="basic-company-info-section"
                  sectionNumber={1}
                  title="Basic Company Information"
                  description="Provide your company's legal and contact details. Fields marked with * are required."
                  headerIcon={
                    <IconBuildingCommunity size={22} color={PROFILE_SECTION.gold} stroke={1.5} />
                  }
                >
                  <BasicCompanyInfo
                    formData={state.formData}
                    updateForm={state.updateForm}
                    logo={state.logo}
                    setLogo={state.handleLogoChange}
                    countryOptions={countryOptions}
                    existingLogo={state.existingLogo}
                    onRemoveLogo={state.handleRemoveLogo}
                    isFieldLocked={isFieldLocked}
                    rightOfLogoContent={
                      <Flex gap="sm" wrap="wrap" justify="flex-end" align="stretch">
                        <Paper
                          p="sm"
                          radius="lg"
                          w={{ base: "100%", md: "auto" }}
                          style={{
                            border: "1px solid #E5E7EB",
                            flex: "0 0 auto",
                            minWidth: 200,
                          }}
                        >
                          <Text fz="xs" c="slate.6" tt="uppercase" fw={500}>
                            Company ID
                          </Text>
                          <Text fz="md" fw={600} c="primary.7">
                            {state.companyGtid || "N/A"}
                          </Text>
                        </Paper>

                        {mode === "create" && (
                          <Paper
                            p="sm"
                            radius="lg"
                            w={{ base: "100%", md: 360 }}
                            bg="linear-gradient(to bottom, #F0FDF4, #FFFFFF)"
                            style={{
                              border: "1px solid #B9F8CF",
                              flex: "0 0 auto",
                              maxWidth: "100%",
                            }}
                          >
                            <Flex gap="sm" align="flex-start">
                              <ThemeIcon size={24} radius="xl" color="teal" variant="light">
                                <IconGift size={14} />
                              </ThemeIcon>
                              <Box>
                                <Text fz="sm" fw={600} mb={4}>
                                  20 Credits Joining Bonus
                                </Text>
                                <Text fz="xs" c="slate.6" style={{ whiteSpace: "normal" }}>
                                  Complete your profile to unlock 20 free credits
                                </Text>
                              </Box>
                            </Flex>
                          </Paper>
                        )}
                      </Flex>
                    }
                  />
                </ProfileFormSection>

                <ProfileFormSection
                  id="business-overview-section"
                  sectionNumber={2}
                  title="Business Overview"
                  description="Describe your company's business focus, industry, and operating model."
                  headerIcon={<IconBuilding size={22} color={PROFILE_SECTION.gold} stroke={1.5} />}
                >
                  <BusinessOverview
                    formData={state.formData}
                    updateForm={state.updateForm}
                    industryOptions={industryOptions}
                  />
                </ProfileFormSection>

                <ProfileFormSection
                  id="products-services-section"
                  sectionNumber={3}
                  title="Products & Services"
                  description="List your company's key products and services."
                  headerIcon={<IconPackage size={22} color={PROFILE_SECTION.gold} stroke={1.5} />}
                >
                  <ProductsServicesTabContent
                    isViewMode={false}
                    products={state.products}
                    services={state.services}
                    onAddProduct={state.handleAddProduct}
                    onUpdateProduct={state.handleUpdateProduct}
                    onRemoveProduct={state.handleRemoveProduct}
                    onAddService={state.handleAddService}
                    onUpdateService={state.handleUpdateService}
                    onRemoveService={state.handleRemoveService}
                    productErrors={state.productErrors}
                    serviceErrors={state.serviceErrors}
                  />
                </ProfileFormSection>

                <ProfileFormSection
                  id="capabilities-offerings-section"
                  sectionNumber={4}
                  title="Capabilities & Offerings"
                  description="Define your core manufacturing capabilities, technologies, and unique differentiators."
                  headerIcon={
                    <IconTool
                      size={22}
                      color={PROFILE_SECTION.gold}
                      stroke={1.5}
                      style={{ transform: "scaleX(-1)" }}
                    />
                  }
                >
                  <CapabilitiesOfferings
                    formData={state.formData}
                    updateForm={state.updateForm}
                    coreCapabilityInput={state.coreCapabilityInput}
                    setCoreCapabilityInput={state.setCoreCapabilityInput}
                    secondaryCapabilityInput={state.secondaryCapabilityInput}
                    setSecondaryCapabilityInput={state.setSecondaryCapabilityInput}
                    technologyInput={state.technologyInput}
                    setTechnologyInput={state.setTechnologyInput}
                    addTag={state.addTag}
                    removeTag={state.removeTag}
                  />
                </ProfileFormSection>

                <ProfileFormSection
                  id="geography-markets-section"
                  sectionNumber={5}
                  title="Geography & Markets"
                  description="Define your geographic presence, operating regions, and target markets."
                  headerIcon={<IconMapPin size={22} color={PROFILE_SECTION.gold} stroke={1.5} />}
                >
                  <GeographyMarkets
                    formData={state.formData}
                    updateForm={state.updateForm}
                    countryOptions={countryOptions}
                    regionOptions={regionOptions}
                  />
                </ProfileFormSection>

                <ProfileFormSection
                  id="personal-details-section"
                  sectionNumber={6}
                  title="Personal Details"
                  description="Provide your personal information to be shared with matched companies. Fields marked with * are required."
                  headerIcon={<IconUser size={22} color={PROFILE_SECTION.gold} stroke={1.5} />}
                >
                  <PersonalDetails
                    formData={state.formData}
                    updateForm={state.updateForm}
                    isFieldLocked={isFieldLocked}
                  />
                </ProfileFormSection>

                <Flex justify="flex-end">
                  <Button
                    size="md"
                    onClick={state.handleSubmit}
                    loading={state.isSubmitting}
                    id="create-profile-submit-btn"
                    style={{ display: "none" }}
                  >
                    {mode === "edit" ? "Save Changes" : "Create Profile"}
                  </Button>
                </Flex>
              </Tabs.Panel>

              <Tabs.Panel value="images" px={0} pt="md" pb="xl">
                <Box id="images-section">
                  <ImagesTabContent
                    company={{ id: state.companyId, images: state.existingImages }}
                    activeTab={state.activeTab}
                    aiImages={state.aiImages}
                    removeAiImage={state.removeAiImage}
                    refreshCompany={async () => {
                      const id = state.companyId;
                      if (id) {
                        const companyData = await trpc.company.getById.query({ companyId: id });
                        if ((companyData as any).images) {
                          state.setExistingImages((companyData as any).images);
                        }
                      }
                    }}
                  />
                </Box>

                <Divider my="xl" />

                <Box id="documents-section">
                  <DocumentsTabContent
                    isViewMode={false}
                    additionalDocs={state.additionalDocs}
                    existingDocs={state.existingDocs}
                    onAddAdditionalDoc={state.handleAddAdditionalDoc}
                    onUpdateAdditionalDoc={state.handleUpdateAdditionalDoc}
                    onRemoveAdditionalDoc={state.handleRemoveAdditionalDoc}
                    onDeleteExistingDoc={state.handleDeleteExistingDoc}
                  />
                </Box>
              </Tabs.Panel>
            </Box>
          </Tabs>
          <ProfileGenerateModal
            opened={state.showIntroModal}
            onClose={() => state.setShowIntroModal(false)}
            onGenerate={handleOpenCreateModal}
            variant={mode === "edit" ? "edit" : "create"}
          />

          <CreateProfileModal
            opened={state.showCreateModal}
            onClose={() => state.setShowCreateModal(false)}
            variant={mode === "edit" ? "edit" : "create"}
            onSuccess={(data, meta) => {
              setIsGeneratingProfile(false);
              handleAiProfileGenerated(data, meta);
            }}
            onGeneratingStart={() => setIsGeneratingProfile(true)}
            onGeneratingEnd={() => setIsGeneratingProfile(false)}
          />

          <MultiStepLoader
            loading={isGeneratingProfile}
            generationKey={isGeneratingProfile ? 1 : 0}
            duration={3500}
            loadingStates={[
              { text: "Analyzing your company information" },
              { text: "Identifying industry and capabilities" },
              { text: "Building your business overview" },
              { text: "Mapping geography and markets" },
              { text: "Generating products and services" },
              { text: "Finalizing your profile" },
            ]}
          />
        </Box>
      </Flex>
    </Flex>
  );
}

export default CreateProfilePage;
